import { Fragment, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AxiosError } from 'axios';
import { useQueryClient } from '@tanstack/react-query';

import { useGetAuthApi, usePostAuthApi } from '@/hooks/UseApiCalls';
import { MailDocumentsDtoIn, TrashMailDocumentsDtoIn } from '@/models/in/MailDocumentsDtoIn';
import NoteDialog from '../NoteDialog';
import styles from './FundBlade.module.css';

const serverMessage = (error: Error): string | null => {
  const data = (error as AxiosError).response?.data;
  return typeof data === 'string' ? data : null;
};

interface MailDocumentsSectionProps {
  fundId: number;
}

/**
 * HAT-1426: the emails in the office mailboxes that hold a frozen fund's documents. The daily
 * search fills the list; the office reads each match and why it matched, sets aside the ones that
 * are not this fund's, and moves the rest to Trash with a note. Gmail empties Trash after 30 days,
 * which is the destruction - until then a wrong match can be restored from Gmail itself.
 */
const MailDocumentsSection = ({ fundId }: MailDocumentsSectionProps) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const url = `funds/${fundId}/management/mail-documents`;

  const { data, isLoading } = useGetAuthApi<MailDocumentsDtoIn>(url);
  const [isTrashNoteOpen, setIsTrashNoteOpen] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const onError = (failure: Error) => setError(serverMessage(failure) ?? t('fundsManagement.saveError'));

  const scan = usePostAuthApi<MailDocumentsDtoIn, object>(`${url}/scan`, {
    onSuccess: () => {
      setError(null);
      setMessage(null);
      queryClient.invalidateQueries({ queryKey: [url] });
    },
    onError,
  });

  const keep = usePostAuthApi<MailDocumentsDtoIn, { findingId: number }>(`${url}/keep`, {
    onSuccess: () => {
      setError(null);
      queryClient.invalidateQueries({ queryKey: [url] });
    },
    onError,
  });

  const trash = usePostAuthApi<TrashMailDocumentsDtoIn, { note: string; findingIds: number[] }>(`${url}/trash`, {
    onSuccess: (result) => {
      setIsTrashNoteOpen(false);
      setError(result.failed > 0 ? t('fundsManagement.mailDocuments.trashPartial', { failed: result.failed }) : null);
      setMessage(t('fundsManagement.mailDocuments.trashResult', { trashed: result.trashed }));
      queryClient.invalidateQueries({ queryKey: [url] });
      // The fund's history gained a line.
      queryClient.invalidateQueries({ queryKey: [`funds/${fundId}/management`] });
    },
    onError: (failure) => {
      setIsTrashNoteOpen(false);
      onError(failure);
    },
  });

  const formatDate = (value?: string | null) => (value ? new Date(value).toLocaleDateString('he-IL') : '');

  if (isLoading || !data) {
    return <p className={styles.emptyText}>{t('loading')}...</p>;
  }

  const pending = data.findings.filter((finding) => finding.status === 'Pending');
  const decided = data.findings.filter((finding) => finding.status !== 'Pending');
  const busy = scan.isPending || keep.isPending || trash.isPending;

  return (
    <div>
      <h2 className={styles.sectionTitle}>{t('fundsManagement.mailDocuments.title')}</h2>

      {!data.isConfigured ? (
        <p className={`${styles.emptyText} mt-3`}>{t('fundsManagement.mailDocuments.notConfigured')}</p>
      ) : (
        <div className={`${styles.detailsList} mt-3`}>
          <span className={styles.detailLabel}>
            {data.lastScannedAt
              ? t('fundsManagement.mailDocuments.lastScanned', {
                  date: formatDate(data.lastScannedAt),
                  mailboxes: data.mailboxes.join(', '),
                })
              : t('fundsManagement.mailDocuments.neverScanned')}
          </span>

          {/* Only when nothing was ever found - after a trash the decided list below says what happened. */}
          {data.lastScannedAt && data.findings.length === 0 && (
            <span className={styles.detailValue}>{t('fundsManagement.mailDocuments.nothingFound')}</span>
          )}

          {pending.length > 0 && (
            <div className={styles.pendingRequest}>
              <strong>{t('fundsManagement.mailDocuments.pendingCount', { count: pending.length })}</strong>
              {pending.map((finding) => (
                <div key={finding.id} className={styles.personRow}>
                  <span className={styles.personName}>{finding.subject || '—'}</span>
                  {/* Each part isolated: "שם <address@x>" next to an address otherwise reorders under RTL. */}
                  <span className={styles.personMeta}>
                    {[
                      formatDate(finding.messageDate),
                      finding.from ? t('fundsManagement.mailDocuments.from', { from: finding.from }) : '',
                      finding.mailbox,
                    ]
                      .filter(Boolean)
                      .map((part, index) => (
                        <Fragment key={index}>
                          {index > 0 && ' • '}
                          <bdi>{part}</bdi>
                        </Fragment>
                      ))}
                  </span>
                  {finding.attachmentNames.length > 0 && (
                    <span className={styles.personMeta}>
                      {t('fundsManagement.mailDocuments.attachments', { names: finding.attachmentNames.join(', ') })}
                    </span>
                  )}
                  {finding.matchReason && (
                    <span className={styles.personMeta}>
                      {t('fundsManagement.mailDocuments.reason', { reason: finding.matchReason })}
                    </span>
                  )}
                  <div>
                    <button
                      type="button"
                      className={styles.pillOutline}
                      disabled={busy}
                      onClick={() => keep.mutate({ findingId: finding.id })}
                    >
                      {t('fundsManagement.mailDocuments.keep')}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {decided.length > 0 && (
            <div className={styles.personList}>
              {decided.map((finding) => (
                <span key={finding.id} className={styles.personMeta}>
                  {finding.subject || '—'} · {t(`fundsManagement.mailDocuments.status.${finding.status}`)}
                  {finding.decidedAt ? ` · ${formatDate(finding.decidedAt)}` : ''}
                </span>
              ))}
            </div>
          )}

          <div className={styles.actionsRow}>
            <button
              type="button"
              className={styles.pillOutline}
              disabled={busy}
              onClick={() => scan.mutate({})}
            >
              {scan.isPending ? t('fundsManagement.mailDocuments.scanning') : t('fundsManagement.mailDocuments.scan')}
            </button>
            {pending.length > 0 && (
              <button
                type="button"
                className={styles.pillPrimary}
                disabled={busy}
                onClick={() => {
                  setError(null);
                  setMessage(null);
                  setIsTrashNoteOpen(true);
                }}
              >
                {t('fundsManagement.mailDocuments.trash', { count: pending.length })}
              </button>
            )}
          </div>
        </div>
      )}

      {message && <p className={`${styles.actionResult} mt-2`}>{message}</p>}
      {error && <p className={`${styles.actionError} mt-2`}>{error}</p>}

      {/* Exactly the emails on screen: a search that ran after the list was drawn adds nothing to this. */}
      <NoteDialog
        open={isTrashNoteOpen}
        onClose={() => setIsTrashNoteOpen(false)}
        title={t('fundsManagement.mailDocuments.trashTitle')}
        message={t('fundsManagement.mailDocuments.trashMessage', { id: fundId, count: pending.length })}
        confirmText={t('fundsManagement.mailDocuments.trashConfirm')}
        isLoading={trash.isPending}
        onConfirm={(note) => trash.mutate({ note, findingIds: pending.map((finding) => finding.id) })}
      />
    </div>
  );
};

export default MailDocumentsSection;
